'use client'

import { type ReactNode, createContext, useRef, useContext, useLayoutEffect } from 'react';
import { useStore } from 'zustand';
import { createGlobalStore } from '~/stores/store';

export const StoreContext = createContext(undefined);

export interface StoreProviderProps {
  children: ReactNode
}

export const StoreProvider = ({
  children,
}: StoreProviderProps) => {
  const storeRef = useRef<any>(null);
  if (storeRef.current === null) {
    storeRef.current = createGlobalStore();
  }

  useLayoutEffect(() => {
    // hydrate the store
    storeRef.current.persist?.rehydrate();
  }, []);

  return (
    <StoreContext.Provider value={storeRef.current}>
      {children}
    </StoreContext.Provider>
  )
}

export const useGlobalStore = <T,>(
  selector: (store: any) => T,
): T => {
  const storeContext = useContext(StoreContext);

  if (!storeContext) {
    throw new Error(`useGlobalStore must be used within StoreProvider`);
  }

  return useStore(storeContext, selector);
}

export const getStoreContext = () => {
  const storeContext = useContext(StoreContext);

  if (!storeContext) {
    throw new Error(`getStoreContext must be used within StoreProvider`);
  }

  return storeContext;
}
